/**
 * Pinned from `/Users/saher/code/cafe/Tarabeeza-Backend` (branch `dev`, read-only
 * source), ADR-0032:
 *   - src/modules/copilot/copilot.controller.ts
 *   - src/modules/copilot/copilot-orchestrator.service.ts (`CopilotAnswer`)
 *   - src/modules/copilot/dto/ask-copilot.dto.ts
 *   - src/modules/copilot/model/copilot-model.port.ts (`CopilotStep`, history)
 *   - src/modules/copilot/tools/types.ts (`ToolOutcome`)
 * One file, so a later change on that side is a one-file fix here.
 *
 * ADR-0032 in three sentences that shape this screen:
 *   1. One route, `POST /copilot/ask`, gated on `copilot.use`
 *      (TENANT_OWNER/TENANT_ADMIN by default) — everything the answer may
 *      actually READ is still decided per-call by `reports.view`/`.view_cost`
 *      through the same semantic engine the report builder runs under.
 *   2. It refuses rather than invents. A refusal is correct behaviour and is
 *      rendered as an answer, never as an error — UNLESS a tool call inside
 *      it actually faulted (`outcome.status === 'error'`), which is a system
 *      fault, not a governed "no".
 *   3. `citedMetrics` names only measures/dimensions the orchestrator itself
 *      verified came back from a tool call with `status: 'ok'` in this same
 *      conversation — show them; they are what makes the answer checkable.
 *      There is no LLM provider configured; every answer here runs against a
 *      small deterministic stub, and this is not a chat — one question in,
 *      one governed answer or refusal out, no stored conversation history.
 */

export interface AskCopilotInput {
  question: string;
}

export type ToolOutcome =
  | {
      status: 'ok';
      data: unknown;
      /** Registry keys (measures/dimensions) this call's answer may be traced to. */
      citedMetrics: string[];
      /** The branch ceiling the read actually ran under. */
      scope: { branchIds: string[] | null };
    }
  | { status: 'refused'; reason: string; missing?: string[] }
  /** A system fault (a tenant-binding assertion, a timeout) — never reshaped into "no data". */
  | { status: 'error'; reason: string };

export interface CopilotToolCallRequest {
  tool: string;
  arguments: Record<string, unknown>;
}

export interface CopilotHistoryEntry {
  call: CopilotToolCallRequest;
  outcome: ToolOutcome;
}

/** `POST /copilot/ask` response, verbatim `CopilotAnswer`. */
export interface CopilotAnswer {
  conversationId: string;
  refused: boolean;
  text: string;
  /** Empty on a refusal. */
  citedMetrics: string[];
  /** Null on a refusal. */
  scope: { branchIds: string[] | null } | null;
  /** Full tool-call trace — how the answer (or refusal) was actually reached. */
  toolCalls: CopilotHistoryEntry[];
}

/**
 * `refused: true` covers both a governed "no" (out of domain, a write, a
 * gated measure, an ungrounded citation the orchestrator itself caught) AND
 * a system fault (a tool call that threw). The response shape does not carry
 * that distinction as its own field — see `copilot-orchestrator.service.ts`'s
 * `finish()`, whose `outcome` argument never reaches the client — so this
 * reads it back out of the trace: any `status: 'error'` entry means the
 * refusal was a fault, not a decision, which is the one case worth a
 * "try again" rather than a plain "no".
 */
export function isSystemFault(answer: CopilotAnswer): boolean {
  return answer.refused && answer.toolCalls.some((entry) => entry.outcome.status === 'error');
}

export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.filter(Boolean).join(' · ');
  return (typeof message === 'string' && message.trim()) || fallback;
}
